import {
  ArrowDown,
  ArrowUp,
  BriefcaseOutline,
  CardOutline,
  ChatboxEllipses,
  DocumentText,
  HelpBuoy,
  LockClosedOutline,
  LogOutOutline,
  NotificationsOutline,
  PhonePortraitOutline,
  Search,
  ShieldHalf,
  SparklesOutline,
  SwapVertical,
  Wallet,
  WalletOutline,
  ArrowDownCircleOutline,
  ArrowUpCircleOutline,
  CardOutline as PaymentCardOutline,
  ReceiptOutline,
} from "@raresail/react-ionicons";
import { TMenuItem } from "../@types/common";
import {
  BellIcon,
  CardIcon,
  CaseIcon,
  HomeIcon,
  ProfileIcon,
  WalletIcon,
} from "../assets/svg/Svgs";
import { useAuth } from "../components/context/AuthContext";
import { getListRoute, returnObject } from "../utils/helpers";
import { openBottomSheet, openModalForm } from "../utils/modalUtils";
import { IsBorrower, IsInvestor } from "../utils/permissionUtils";
import {
  MANUAL_DEPOSITS,
  MANUAL_WITHDRAWALS,
  MANUAL_REPAYMENTS,
  ACCOUNT,
  AUTOINVEST,
  DEVICES,
  DEVICE_ORDERS,
  HOME,
  INSTITUTIONS,
  INVEST,
  INVESTMENT_PLANS,
  INVESTMENTS,
  LOANS,
  NOTIFICATIONS,
  ONBOARD,
  PASSWORD_RESET,
  TEXT,
  TOP_UP,
  TRANSACTIONS,
  TRANSFER,
  WALLET,
  WITHDRAW,
} from "./constants";

export const NavOptions = () => {
  const items = [
    {
      label: HOME,
      icon: HomeIcon,
      to: "/",
    },
    {
      label: WALLET,
      icon: WalletIcon,
      to: WALLET,
    },
    // Investor: invest page
    {
      label: INVEST,
      icon: CaseIcon,
      to: INVEST,
      visible: IsInvestor(),
    },
    // Investor: managed AutoInvest
    {
      label: "AutoInvest",
      icon: SparklesOutline,
      to: AUTOINVEST,
      visible: IsInvestor(),
    },
    // Borrower: loans
    {
      label: LOANS,
      icon: CardIcon,
      to: LOANS,
      visible: IsBorrower(),
    },
    // Borrower: device catalog
    {
      label: DEVICES,
      icon: PhonePortraitOutline,
      to: getListRoute(DEVICES),
      visible: IsBorrower(),
    },
    {
      label: ACCOUNT,
      icon: ProfileIcon,
      to: ACCOUNT,
    },
  ];

  return items;
};

export const WalletActions = () => [
  {
    label: TOP_UP,
    icon: ArrowUp,
    onClick: () => {
      openBottomSheet({ component: "method_picker", source: TOP_UP });
    },
  },
  {
    label: WITHDRAW,
    icon: ArrowDown,
    onClick: () => {
      openBottomSheet({ component: "method_picker", source: WITHDRAW });
    },
  },
  {
    label: TRANSFER,
    icon: SwapVertical,
    onClick: () => {
      openModalForm({ source: TRANSFER });
    },
  },
  // Investor: invest shortcut (only investors see this)
  ...returnObject(IsInvestor(), [
    {
      label: INVEST,
      icon: BriefcaseOutline,
      to: INVEST,
    },
  ]),
  // Borrower: loans shortcut (only borrowers see this)
  ...returnObject(IsBorrower(), [
    {
      label: `Your ${LOANS}`,
      icon: CardOutline,
      to: getListRoute(LOANS),
    },
  ]),
];

export const UtilityOptions = () => {
  return [
    {
      label: TRANSACTIONS,
      icon: CardIcon,
      to: getListRoute(TRANSACTIONS),
    },
    // ── NEW: Manual operations history ──
    {
      label: "Deposits",
      icon: ArrowDownCircleOutline,
      to: getListRoute(MANUAL_DEPOSITS),
    },
    {
      label: "Withdrawals",
      icon: ArrowUpCircleOutline,
      to: getListRoute(MANUAL_WITHDRAWALS),
    },
    {
      label: "Manual Repayments",
      icon: ReceiptOutline,
      to: getListRoute(MANUAL_REPAYMENTS),
      visible: IsBorrower(),
    },

    // Investor: portfolio
    {
      label: INVESTMENTS,
      icon: CaseIcon,
      to: getListRoute(INVESTMENTS),
      visible: IsInvestor(),
    },
    // Borrower: device orders
    {
      label: "My BNPL Orders",
      icon: PhonePortraitOutline,
      to: getListRoute(DEVICE_ORDERS),
      visible: IsBorrower(),
    },
    // Notifications (for all users)
    {
      label: NOTIFICATIONS,
      icon: NotificationsOutline,
      to: `/${NOTIFICATIONS}`,
    },
    // Investor: marketplace
    {
      label: "Marketplace",
      icon: CaseIcon,
      to: getListRoute(INSTITUTIONS),
      visible: IsInvestor(),
    },
  ];
};

export const helpOptions = [
  {
    label: "Financial Advisor",
    icon: SparklesOutline,
    to: "/advisor",
  },
  // {
  //   label: "Live Chat",
  //   icon: ChatboxEllipses,
  // },
  {
    label: "help center",
    icon: HelpBuoy,
    to: "/help",
  },
];

export const loanActions = [
  {
    label: "apply loan",
    icon: BriefcaseOutline,
    onClick: () => {
      openModalForm({ source: LOANS });
    },
  },
  {
    label: `Your ${WALLET}`,
    icon: WalletOutline,
    to: `/${WALLET}`,
  },
  {
    label: "finance device",
    icon: PhonePortraitOutline,
    to: getListRoute(DEVICES),
  },
];

const learnOptions = [
  {
    label: "Financial literacy",
    icon: DocumentText,
    to: "/learn",
  },
];

export const MainMenu = () => {
  return [
    {
      title: "general",
      items: NavOptions(),
    },
    {
      title: "utilities",
      items: UtilityOptions(),
    },
    {
      title: "support",
      items: helpOptions,
    },
    {
      title: "learn",
      items: learnOptions,
    },
  ];
};

export const AccountOptions = (): TMenuItem[] => {
  const { logout } = useAuth();
  return [
    { label: "My Profile", icon: ProfileIcon, to: `/${ONBOARD}` },
    {
      label: "Payment Methods",
      icon: PaymentCardOutline,
      to: "/payment-methods",
    },
    {
      label: "Wallet Info",
      icon: Wallet,
      onClick: () =>
        openBottomSheet({
          component: "details",
          title: "Wallet Details",
          source: WALLET,
        }),
    },
    {
      label: "Account Security",
      icon: ShieldHalf,
      to: "",
      children: [
        {
          label: "password reset",
          icon: LockClosedOutline,
          onClick: () => openModalForm({ source: PASSWORD_RESET }),
        },
        {
          label: "change pin",
          icon: LockClosedOutline,
        },
      ],
    },
    {
      label: "Notification Preferences",
      icon: NotificationsOutline,
      to: "/settings/notifications",
    },
    { label: "Terms of Service", icon: DocumentText, to: "/legal/terms" },
    { label: "Logout", icon: LogOutOutline, onClick: logout },
  ];
};

export const searchFilterField = {
  field: "search",
  type: TEXT,
  props: { prefix: <Search color="grey" />, noLabel: true },
};